import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "shared/components/ui/alert-dialog";
import { Input } from "shared/components/ui/input";
import { Label } from "shared/components/ui/label";
import { AssistantService } from "shared/services/AssistantService";
import {
  ReportSectionsTree,
  ReportSections
} from "../../single/components/ReportSectionsTree";
import { FinecoButton } from "pages/assistant/components/FinecoButton";
import colorsStore from "shared/store/colorsStore";
import { createComparisonReportParameters } from "utils/comparisonUtils";

type GenerateComparisonReportProps = {
  threadId: string;
  portfolioId1: string;
  portfolioId2: string;
  children: React.ReactNode;
  onGenerate: (data: any, fromReport?: boolean) => void;
};

export const GenerateComparisonReport = forwardRef<
  { generate: () => void },
  GenerateComparisonReportProps
>(({ threadId, portfolioId1, portfolioId2, children, onGenerate }, ref) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const { t } = useTranslation();
  const [bankerName, setBankerName] = useState("");
  const [bankerRole, setBankerRole] = useState("");
  const [proposedValue, setProposedValue] = useState(100000);
  const [proposedValue2, setProposedValue2] = useState(100000);
  const [isLoading, setIsLoading] = useState(true);
  const [reportSections, setReportSections] = useState<ReportSections>({});

  useImperativeHandle(ref, () => ({
    generate: () => {
      handleGenerate(false);
    },
  }));

  useEffect(() => {
    const loadReportParameters = async () => {
      try {
        let response;

        if (portfolioId1 && portfolioId2) {
          // Create enriched comparison parameters using v2 API
          response = await createComparisonReportParameters(
            (portfolioId: string) => AssistantService.fetchReportParameters(portfolioId),
            portfolioId1,
            portfolioId2
          );
        } else {
          console.warn("No portfolio IDs available for comparison report parameters");
          // Use default parameters
          response = { banker_name: "", banker_role: "", report_sections: {} };
        }

        setBankerName(response.banker_name || "");
        setBankerRole(response.banker_role || "");

        // Process sections to add hidden flag
        const processedSections = JSON.parse(
          JSON.stringify(response.report_sections || {}),
        );
        setReportSections(processedSections);
      } catch (error) {
        console.error("Failed to load comparison report parameters:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadReportParameters();
  }, [threadId, portfolioId1, portfolioId2]);

  const handleGenerate = (fromReport: boolean = false) => {
    const data = {
      banker_name: bankerName,
      banker_role: bankerRole,
      portfolio_id_1: portfolioId1,
      portfolio_id_2: portfolioId2,
      proposed_value: proposedValue,
      proposed_value_2: proposedValue2,
      ...(fromReport && { report_sections: reportSections }),
    };
    onGenerate(data, fromReport);
  };

  const hasSelectedSections = () => {
    return Object.values(reportSections).some((section) => section.visible && section.selected);
  };

  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent
        style={{
          backgroundColor: "white",
          color: colorsConfig.threadHistoryCopy || "#ffffff",
          maxWidth: "600px",
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle style={{ color: colorsConfig.finecoBlack }}>
            {t("generateReport.titleComparison")}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <Label
                    htmlFor="bankerRole"
                    style={{ color: colorsConfig.finecoBlack }}
                  >
                    {t("generateReport.bankerRole")}
                  </Label>
                  <select
                    id="bankerRole"
                    value={bankerRole}
                    onChange={(e) => setBankerRole(e.target.value)}
                    className="w-full rounded border p-2"
                  >
                    <option value="Personal Financial Advisor">Personal Financial Advisor</option>
                    <option value="Private Banker">Private Banker</option>
                    <option value="Senior Private Banker">Senior Private Banker</option>
                    <option value="Senior Private Banker e Ambassador">Senior Private Banker e Ambassador</option>
                    <option value="Group Manager">Group Manager</option>
                    <option value="Area Manager">Area Manager</option>
                  </select>
                </div>
                <div>
                  <Label
                    htmlFor="proposedValue"
                    style={{ color: colorsConfig.finecoBlack }}
                  >
                    {t("generateReport.proposedValue")} (Portafoglio 1)
                  </Label>
                  <Input
                    id="proposedValue"
                    type="number"
                    value={proposedValue}
                    onChange={(e) => setProposedValue(Number(e.target.value))}
                    className="w-full text-right"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="proposedValue2"
                    style={{ color: colorsConfig.finecoBlack }}
                  >
                    {t("generateReport.proposedValue")} (Portafoglio 2)
                  </Label>
                  <Input
                    id="proposedValue2"
                    type="number"
                    value={proposedValue2}
                    onChange={(e) => setProposedValue2(Number(e.target.value))}
                    className="w-full text-right"
                  />
                </div>
              </div>
              <div>
                {/* Empty second column */}
              </div>
            </div>

            <div className="mt-8">
              <h3
                className="mb-4 font-medium"
                style={{ color: colorsConfig.finecoBlack }}
              >
                {t("generateReport.sections")}
              </h3>
              <ReportSectionsTree
                sections={reportSections}
                onChange={setReportSections}
              />
            </div>
          </div>
        </AlertDialogDescription>
        <AlertDialogFooter>
          <FinecoButton
            click={() => {
              setOpen(false);
            }}
            white={true}
            copy={t("global.cancel").toUpperCase()}
          />
          <FinecoButton
            disabled={!hasSelectedSections()}
            click={() => {
              handleGenerate(true);
              setOpen(false);
            }}
            copy={t("generateReport.generate").toUpperCase()}
          />
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
});